<script lang="ts" setup>

import { storeToRefs } from 'pinia';
import { ref } from 'vue';

import opensea from '@/assets/images/opensea.webp';
import useMetaCollectionSlugId from '@/composables/meta/useMetaCollectionSlugId';
import { useCartStore } from '@/stores/cart/useCartStore';
import { useUserStore } from '@/stores/user/useUserStore';
import { formatValue } from '@/utils/helpers';
import { computed, useAsyncData, useDevice, useProviderConfig, useRoute } from '#imports';

type RouteStateType = Pick<ApiPagination, 'page' | 'size'>;
type Tab<T> = {
    label: string;
    value: T;
};

const enum OwnerTabEnum {
    SELL,
    BUY
}

const ownerTabs: Tab<OwnerTabEnum>[] = [
    {
        label: 'Sell',
        value: OwnerTabEnum.SELL
    },
    {
        label: 'Buy',
        value: OwnerTabEnum.BUY
    }
];

const tabs: Tab<string>[] = [
    // {
    //     value: 'Price history',
    //     label: 'Price history'
    // },
    {
        value: 'Offers',
        label: 'Offers'
    },
    {
        value: 'Activities',
        label: 'Activities'
    },
    {
        value: 'Listings',
        label: 'Listings'
    }
];

const {
    QueryWTFApiKey,
    nft,
    collection
} = useWTFApi();
const route = useRoute();
const {
    utils: { isEvmErc1155 }
} = useProviderConfig()(['utils']);

const collectionSlug = route.params.collection as string;
const identifier = route.params.identifier as string;
const userStore = useUserStore();

const { user } = storeToRefs(userStore);
const {
    nft: nftMarketApi,
    QueryEvmKey
} = useEVMApi();

const isMakeOfferModalOpen = ref(false);
const isListingModalOpen = ref(false);
const isOwners1155ModalOpen = ref(false);
const selectedOwnerTab = ref<OwnerTabEnum>(OwnerTabEnum.BUY);
const activeTab = ref<typeof tabs[number]['value']>('Offers');
const paramsForOwnersModal = ref<RouteStateType>({
    page: 1,
    size: 6
});

const counterErc1155 = ref(1);
const { isDesktop } = useDevice();
const cartStore = useCartStore();
const { cart } = storeToRefs(cartStore);

const {
    'data': collectionProfile,
    'status': statuscollectionProfile,
    'error': errorcollectionProfile
} = useAsyncData(
    `${QueryWTFApiKey.COLLECTION}-${collectionSlug}`,
    async () => {
        const response = await collection.collectionGet(collectionSlug);
        return response.data;
    }
);

const {
    'data': nftData,
    'status': statusNftData,
    'error': errorNFTData,
    refresh: refreshNftData
} = useAsyncData(
    `${QueryWTFApiKey.NFT}-${collectionSlug}-${identifier}-${user.value}`,
    async () => {
        if (collectionSlug && user && identifier) {
            const response = await nft.getNft(
                collectionSlug,
                identifier,
                user.value
            );
            return response.data;
        }
    },
    { watch: [user] }
);

const {
    'data': nftLiteData,
    'status': statusLiteNftData,
    'error': errorLiteNFTData,
    refresh: refreshLiteNftData
} = useAsyncData(
    `${QueryEvmKey.NFT_LITE}-${collectionSlug}-${identifier}`,
    async () => {
        if (collectionSlug && identifier) {
            const response = await nftMarketApi.getNftDetails(
                collectionSlug,
                identifier
            );
            return response.data;
        }
    }
);

const {
    'data': nftList,
    'status': statusNftList,
    'error': errorNFTList
} = useAsyncData(
    `${QueryWTFApiKey.NFT_LIST}-${collectionSlug}-${identifier}`,
    async () => {
        if (route.params.collection) {
            const response = await nftMarketApi.getNftList(route.params.collection as string, user.value
                ? {
                        owner: user.value,
                        listed: false
                    }
                : {
                        listed: false
                    });
            return response.data;
        }
        return [];
    },
    {
        watch: [user],
        default: () => []
    }
);

const {
    data: nftOwners,
    status: nftOwnersStatus,
    error: errorNftOwners
} = useAsyncData(
    `${QueryWTFApiKey.NFT_OWNERS}-${collectionSlug}-${identifier}`,
    async () => {
        const {
            data,
            pagination
        } = await nft.getNftOwners(
            collectionSlug,
            identifier,
            paramsForOwnersModal.value);
        return {
            data,
            pagination
        };
    },
    {
        default: () => ({
            data: [],
            pagination: { total: 0 }
        }),
        watch: [paramsForOwnersModal]
    }
);

watch([user, nftData], ([newUser, newNftData]) => {
    if (!newUser || !newNftData?.ownedCount) {
        selectedOwnerTab.value = OwnerTabEnum.BUY;
    }
}, { immediate: true });

const openModal = async (type: 'offer' | 'listing' | 'owners') => {
    if (type === 'owners') {
        isOwners1155ModalOpen.value = true;
        return;
    }

    if (!userStore.user) {
        userStore.showUserIsDisconnect();
        return;
    }

    const modalStateMap = {
        offer: isMakeOfferModalOpen,
        listing: isListingModalOpen
    };

    modalStateMap[type].value = true;
};

const formattedPrice = computed(() => {
    if (!nftLiteData.value?.price?.amount) return '';
    return formatValue(nftLiteData.value.price.amount, { precision: 6 });
});

const existToCart = computed(() => {
    if (!nftData.value?.uuid) return false;
    return cart.value.has(nftData.value.uuid);
});

const unlisted = computed(() => (nftData.value as AppWTFNft)?.price?.amount === 0);
const withCounter = computed(() => isEvmErc1155((nftData.value as AppWTFNft)?.itemType));

const isNftDataLoading = computed(() => statusLiteNftData.value === 'pending' || statusNftData.value === 'pending');
const totalForOwnersModal = computed(() => nftOwners.value?.pagination?.total || 0);
const webSiteUrl = computed(() => {
    if (collectionProfile?.value?.links) return collectionProfile.value.links.find(item => item.title === 'project')?.href ?? null;
    return null;
});

const updateCart = () => {
    if (!nftLiteData.value?.price?.amount || !nftData.value) return;

    cartStore.addToCart({
        uuid: nftData.value.uuid,
        displayImageURL: nftData.value.displayImageURL,
        price: nftLiteData.value.price,
        name: nftData.value.name,
        network: nftData.value.network,
        itemType: nftData.value.itemType,
        amount1155: counterErc1155.value,
        ...(isEvmErc1155(nftData.value.itemType) ? { maxAmount1155: Number(nftLiteData.value.price.amount) } : {})
    });
};

const handleActiveTabChange = (newActiveTab: typeof tabs[number]['value']) => {
    activeTab.value = newActiveTab;
};

const updateQueryParamsForOwnersModal = (newParams: Partial<RouteStateType>) => {
    paramsForOwnersModal.value = { ...paramsForOwnersModal.value, ...newParams };
};
const refresh = async () => {
    if (nftData.value) await nft.updateToRefresh(nftData.value.uuid);
    await refreshLiteNftData();
    await refreshNftData();
};

if (nftData.value) {
    useMetaCollectionSlugId({
        displayImageURL: nftData.value.displayImageURL,
        name: nftData.value.name,
        displayAnimationURL: nftData.value.displayAnimationURL,
        description: nftData.value.description,
        metadata: nftData.value.metadata,
        route: route
    });
}

const tabComponents: Record<typeof tabs[number]['value'], ReturnType<typeof defineAsyncComponent>> = {
    Offers: defineAsyncComponent(() => import('~/components/widgets/info-token/Offers/InfoTokenOffers.vue')),
    Activities: defineAsyncComponent(() => import('~/components/widgets/info-token/Activities/InfoTokenActivities.vue')),
    Listings: defineAsyncComponent(() => import('~/components/widgets/info-token/Listings/InfoTokenListings.vue'))
};

const currentKey = computed(() => {
    if (activeTab.value === 'Activities') {
        return `Activities-${nftData.value?.uuid || ''}`;
    }
    else {
        return `${activeTab.value}-${nftLiteData.value?.identifier || ''}`;
    }
});

const currentProps = computed(() => {
    if (activeTab.value === 'Offers' || activeTab.value === 'Listings') {
        return {
            collectionAddress: nftLiteData.value?.collection.address,
            identifier: nftLiteData.value?.identifier
        };
    }
    if (activeTab.value === 'Activities') {
        return {
            nftData: nftData.value
        };
    }
    return {};
});

const currentComponent = computed(() => {
    return tabComponents[activeTab.value] || null;
});

</script>

<template>
  <Breadcrumbs :data="{
                 name:collectionProfile?.name || '...',
                 slug:collectionSlug
               }"
               :current-position="true"
               class="breadcrumbs"
  />

  <section class="section-token">
    <div class="container-token gap-32 width-full">
      <TokenMediaViewer :url="nftData?.displayAnimationURL || nftData?.displayImageURL"
                        :name="nftData?.name"
                        :is-loading="statusNftData === 'pending'"
      />

      <div class="display-flex flex-column gap-4 content-container z-1">
        <TokenDetails :initial-visibility="true"
                      :is-loading="isNftDataLoading"
                      :address="nftData?.address"
                      :identifier="nftData?.identifier"
                      :item-type="nftData?.itemType"
                      :network="nftData?.network"
        />
        <TokenProperties :is-loading="isNftDataLoading"
                         :properties="nftData?.properties"
                         :initial-visibility="true"
        />
        <TokenAbout :is-loading="isNftDataLoading"
                    :description="nftData?.description"
                    :initial-visibility="true"
        />
      </div>
      <div class="title-block display-flex flex-column gap-8 z-1">
        <TokenCollectionHeader :collection-name="collectionProfile?.name"
                               :collection-img="collectionProfile?.imageUrl"
                               :nft-address="nftData?.address"
        />

        <div class="display-flex align-items-center justify-between">
          <h2 class="display-800-accent">
            {{ nftData?.name || nftData?.identifier }}
          </h2>

          <div class="display-flex align-items-center gap-4 flex-wrap">
            <TokenButtonShare />
            <TokenButtonMore :isLoading="isNftDataLoading"
                             :needLinkToWebsite="Boolean(webSiteUrl)"
                             :link-to-website="webSiteUrl || ''"
                             @refreshMetadata="refresh"

            />
          </div>
        </div>

        <TokenOwners v-if="nftData"
                     items="20.3"
                     :is-evm-erc1155="isEvmErc1155(nftData.itemType)"
                     :address-owner="nftOwners.data[0]?.address"
                     :total-owners="totalForOwnersModal"
                     @on-click="openModal('owners')"
                     :is-loading="nftOwnersStatus === 'pending'"
        />
      </div>

      <div class="grey-block z-1 width-full">
        <div v-if="nftData?.ownedCount"
             class="display-flex owner"
        >
          <ToggleIconGroup v-model="selectedOwnerTab"
                           :items="ownerTabs"
                           class="grow"
          />
          <p class="grow display-flex justify-end align-items-center">
            <span class="compact-600-default">You own {{ nftData?.ownedCount }}</span>
          </p>
        </div>
        <div v-if="selectedOwnerTab === OwnerTabEnum.SELL"
             class="display-flex"
        >
          <div class="display-flex gap-4 grow">
            <h3 class="compact-400-default color-text-grey">
              Data from
            </h3>
            <div class="name-collection-image">
              <ImgMain alt="OpenSea"
                       densities="x1 x2"
                       quality="90"
                       :src="opensea"
              />
            </div>
          </div>
          <Button :size="'size.large'"
                  circle
                  class="grow"
                  color="bg.aviation"
          >
            <button class="compact-600-accent"
                    type="button"
                    @click="() => openModal('listing')"
            >
              List items
            </button>
          </Button>
        </div>
        <div v-if="selectedOwnerTab === OwnerTabEnum.BUY"
             class="display-flex justify-between align-items-center"
        >

          <div class="display-flex flex-column gap-26 grow">
            <div class="display-flex flex-column">
              <div class="display-flex align-items-center gap-4">
                <h3 class="compact-400-default color-text-grey">
                  Current price on
                </h3>
                <div class="name-collection-image">
                  <ImgMain alt="OpenSea"
                           densities="x1 x2"
                           quality="90"
                           :src="opensea"
                  />
                </div>
              </div>

              <TokenPrice v-if="nftLiteData?.price?.amount"
                          :nftPrice="nftLiteData.price"
              />

              <h3 v-else
                  class="display-900-accent-numeric"
              >
                No offers
              </h3>
            </div>
          </div>

          <div class="display-flex flex-column gap-8 grow">
            <Counter v-if="!unlisted && nftData && isEvmErc1155(nftData.itemType) && nftLiteData?.price?.amount"
                     :initial-value="counterErc1155"
                     :max="Number(nftData?.price?.amount)"
                     size="medium"
                     @on-change="(payload: number) => counterErc1155 = payload"
            />

            <div v-if="nftLiteData?.price?.amount"
                 class="display-flex align-items-center"
            >
              <ClientOnly>
                <template #fallback>
                  <div class="display-flex align-items-center width-full gap-4">
                    <Button :color="'bg.aviation'"
                            size="size.medium"
                            class="align-self-stretch width-full"
                            circle
                            is-loading
                    />
                    <Button v-if="isDesktop"
                            :color="'bg.mojito'"
                            circle
                    >
                      <button aria-label="to cart"
                              type="button"
                      >
                        <IconTypes :size="28"
                                   name="icon/cart"
                        />
                      </button>
                    </Button>
                  </div>
                </template>

                <div class="display-flex align-items-center width-full gap-4">
                  <Web3BuyNowButton v-if="nftData"
                                    :item="{
                                      uuid: nftData.uuid,
                                      name: nftData.name,
                                      displayImageURL: nftData?.displayImageURL,
                                      price: nftLiteData.price,
                                      network: nftData.network,
                                      itemType: nftData.itemType,
                                      amount1155: counterErc1155
                                    }"
                                    class="grow align-self-stretch token-cart__btn"
                  >
                    <template v-if="withCounter">
                      <span>Buy {{ counterErc1155 }} now</span>
                    </template>
                    <template v-else>
                      <span>Buy now</span><br>
                      <span class="text-wrap-none">for {{ formattedPrice }} {{
                        nftLiteData?.price?.symbol
                      }}</span>
                    </template>
                  </Web3BuyNowButton>

                  <Button v-if="!existToCart && isDesktop && nftLiteData?.price?.amount"
                          :color="'bg.mojito'"
                          circle
                  >
                    <button aria-label="to cart"
                            type="button"
                            @click="updateCart"
                    >
                      <IconTypes :size="28"
                                 name="icon/cart"
                      />
                    </button>
                  </Button>
                </div>
              </ClientOnly>
            </div>

            <div class="display-flex align-items-center gap-8 justify-end">
              <Button class="width-full"
                      color="bg.secondary"
                      size="size.medium"
              >
                <button class="compact-500-accent text-color-primary text-wrap-none"
                        type="button"
                        @click="() => openModal('offer')"
                >
                  Make offer
                </button>
              </Button>
              <Button v-if="!existToCart && !isDesktop && nftLiteData?.price?.amount"
                      :color="'bg.mojito'"
                      circle
                      size="size.small"
              >
                <button aria-label="to cart"
                        type="button"
                        @click="updateCart"
                >
                  <IconTypes :size="28"
                             name="icon/cart"
                  />
                </button>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div class="info-block grey-block border-rad-second align-self-start z-1 align-self-stretch">
        <Tabs :active-tab="activeTab"
              :tabs="tabs"
              type="local"
              @on-change="handleActiveTabChange"
        />
        <Suspense>
          <KeepAlive>
            <component :is="currentComponent"
                       :key="currentKey"
                       v-bind="currentProps"
            />
          </KeepAlive>

          <template #fallback>
            <div class="grow display-flex justify-center align-center">
              <LoaderDots />
            </div>
          </template>
        </Suspense>
      </div>
    </div>
  </section>

  <section aria-label="collection list">
    <SliderNft :data="nftList"
               :is-error="statusNftList === 'error'"
               :is-loading="statusNftList === 'pending'"
               title="More from this collection"
    />

    <ClientOnly>
      <Teleport to="body">
        <LazyModalCartNetworkDifference />

        <Modal v-if="collectionProfile && nftData"
               v-model:model-value="isMakeOfferModalOpen"
        >
          <ModalMakeOffer v-model:model-value="isMakeOfferModalOpen"
                          :data="{
                            name: nftData.name,
                            address: nftData.address,
                            network: nftData.network,
                            identifier: nftData.identifier,
                            displayImageURL: nftData.displayImageURL,
                            price: nftData.price,
                            uuid: nftData.uuid,
                            itemType: nftData.itemType,
                            slug: collectionSlug
                          }"
                          :fees="collectionProfile.fees"
          />
        </Modal>

        <Modal v-if="collectionProfile && nftData"
               v-model:model-value="isListingModalOpen"
               class="token-modal__listing"
        >
          <ModalListing v-model:model-value="isListingModalOpen"
                        :data="{
                          name: nftData.name,
                          address: nftData.address,
                          network: nftData.network,
                          identifier: nftData.identifier,
                          displayImageURL: nftData.displayImageURL,
                          price: nftData.price,
                          uuid: nftData.uuid,
                          collectionName: collectionProfile?.name,
                          itemType: nftData.itemType,
                          ownedCount: nftData.ownedCount,
                          slug: collectionSlug
                        }"
                        :refresh-nft-data="refreshNftData"
          />
        </Modal>

        <Modal v-if="nftOwners && nftData"
               v-model:model-value="isOwners1155ModalOpen"
               :width-content="600"
        >
          <ModalOwnersInfo :owners="nftOwners.data"
                           :params="paramsForOwnersModal"
                           :total="totalForOwnersModal"
                           @close-modal="isOwners1155ModalOpen = false"
                           @update:queryParams="updateQueryParamsForOwnersModal"
          />
        </Modal>
      </Teleport>
    </ClientOnly>
  </section>
</template>

<style lang="scss" scoped>

:deep(.breadcrumbs) {
  margin-bottom: $spacing-900;
}

.token-modal__listing {
  max-height: 100%;
}

.section-token {
  margin-bottom: 64px;
}

.container-token {
  display: grid;
  grid-template:
    "img title"
    "img price"
    "img info"
    "content1 info";
  grid-template-columns: minmax(auto, 640px) 50%;
  grid-template-rows: 110px auto minmax(230px, 1fr) auto;
  max-width: 1312px;

  &-cart {
    &__btn {
      min-height: 79px;
    }
  }

  @include desktop-1200 {
    grid-template:
      "title title"
      "img price"
      "info info"
      "content1 content1";
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto;
  }

  @include tablet-768 {
    grid-template-areas:
      "title"
      "img"
      "price"
      "info"
      "content1";
    grid-template-columns: 1fr;
    grid-template-rows: auto;
    width: 100%;
  }
}

.content-container {
  grid-area: content1;
}

.title-block {
  grid-area: title;
  padding: 0 24px;
}

.owner {
  margin-bottom: 24px;
  padding-right: 16px;
  padding-bottom: 15px;
  border-bottom: 1px solid var(--border-primary);
}

.grey-block {
  padding: 24px;
  gap: 8px;
  border-radius: 4px 44px 4px 4px;
  background: var(--layer-floor-1);

  @include desktop-1200 {
    padding: 16px 8px 8px;
    border-radius: 4px;
  }

  @include tablet-768 {
    border-radius: 4px 44px 4px 4px;
  }

  @include mobile-576 {
    border-radius: 4px;
  }
}

.info-block {
  display: flex;
  grid-area: info;
  flex-direction: column;
  max-height: 500px;

  :deep(.line-container) {
    margin-bottom: 0;
    padding-top: 0;
  }

  @include mobile-576 {
    overflow: hidden;
  }
}

.name-collection-image {
  width: 28px;
  height: 28px;
  border: var(--image-paranja-stroke, 1px) solid var(--border-paranja, rgb(255 255 255 / 20%));
  border-radius: var(--control-300-radius, 2px);
}

.border-rad-second {
  padding: var(--grid-small, 8px) var(--grid-large, 24px);
  border-radius: var(--radius-300, 4px);
}

:deep(.flicking-viewport) {
  height: auto !important;
}
</style>
